import { DefaultRoles, NonRoleEditableAPIScopes, RoleInterface, isDefaultRoleSlug } from "@swivl-mono/types";

/**
 * Classifies a `RoleInterface` row against the canonical `DefaultRoles`
 * TypeScript map. Used by the role-default-cleanup script (and any audit
 * tooling) to decide whether an existing DB row should be deleted (it
 * mirrors the TS map exactly for the operator-meaningful surface) or
 * renamed to `<slug>-legacy` (operator actually customized something).
 *
 * Divergence rules — what we ignore on purpose:
 *
 *  1. **Name + description.** Operators sometimes tweak labels
 *     ("Administrator" vs "Admin"); the TS map overwrites the display
 *     label on read anyway.
 *  2. **`NonRoleEditableAPIScopes`.** Operators can't change these via
 *     the role UI (the service strips them from incoming updates). Any
 *     disagreement on them is a seeding artifact, not a customization.
 *  3. **Keys present in TS but missing in the DB row.** The TS map
 *     picks up new scopes automatically via `...AllAPIScopePermissions`;
 *     old DB rows never got re-seeded as new scopes were added. A
 *     missing key isn't a customization, it's drift.
 *
 * What counts as divergence: a UI-editable scope present on both sides
 * with disagreeing values. That's the only signal that an operator
 * actually intentionally customized the role.
 *
 *  - `"not-default"` — the row's slug isn't in `DEFAULT_ROLE_SLUGS`.
 *  - `"match"`       — no operator-meaningful divergence; safe to delete.
 *  - `"diverge"`     — operator customized at least one UI-editable
 *                      scope; rename to `<slug>-legacy` to preserve.
 *
 * Pure. No I/O.
 */
export type DefaultRoleDiff = "match" | "diverge" | "not-default";

const NON_EDITABLE_SET: ReadonlySet<string> = new Set(NonRoleEditableAPIScopes as readonly string[]);

export function diffAgainstDefault(row: RoleInterface): DefaultRoleDiff {
  if (!isDefaultRoleSlug(row.slug)) return "not-default";
  const expected = DefaultRoles[row.slug];
  const dbActions = (row.actions ?? {}) as Record<string, string>;
  const tsActions = expected.actions as unknown as Record<string, string>;
  for (const k of Object.keys(dbActions)) {
    if (NON_EDITABLE_SET.has(k)) continue;
    if (!(k in tsActions)) continue;
    if (dbActions[k] !== tsActions[k]) return "diverge";
  }
  return "match";
}
